<template>
  <BgImageLayout :title="`Import title list for product: ${product.name}`">
    <q-card class="bg-white">
      <q-card-section>
        <form class="row q-col-gutter-md m-t-3 m-b-3 justify-content-md-center" @submit.prevent="process()">
          <div class="col-xs-12">
            <p>
              The uploaded file can be uploaded in either the KBART-format or in a custom format, defined by you.
              <br />The custom format allows you to map the columns to the appropriate fieldnames (i.e., Journal name =
              Column B, for instance).
            </p>
            <p>
              You must also specify the period that the title-list covers. So, if you know that the title list will be
              the same for a 3-year agreement term, you can simply set the expiration date of the title-list
              accordingly.
            </p>
            <q-btn color="tertiary" outline download @click="download(kbartTemplateUrl)"
              >Download template file (KBART-format)</q-btn
            >
          </div>

          <div class="col-xs-12 flex items-center">
            <q-icon class="fas fa-info-circle q-mr-sm" color="tertiary" size="1.2rem"></q-icon>
            A title list needs to be less than 10,000 to be uploaded. If more than 10,000 you need to upload it in 2
            title lists.
          </div>

          <div class="col-xs-3">
            <q-input id="name" v-model="formData.name" name="name" label="Name" type="text" outlined />
          </div>
          <div class="col-xs-3">
            <q-file
              v-model="$v.formData.file.$model"
              :error="$v.formData.file.$error"
              :error-message="$v_displayMessage($v.formData.file)"
              clearable
              accept=".xlsx, .xls, .csv"
              label="Choose or drag file here *"
              outlined
            />
          </div>
          <div class="col col-md-3">
            <LmDatePicker
              v-model="$v.formData.valid_from.$model"
              :error="$v.formData.valid_from.$error"
              :error-message="$v_displayMessage($v.formData.valid_from)"
              label="Valid from *"
            />
          </div>
          <div class="col col-md-3">
            <LmDatePicker v-model="formData.valid_to" :disable="!formData.valid_from" clearable label="Valid to" />
          </div>
          <div class="col-xs-12">
            <p>File format</p>
            <q-option-group
              v-model="formData.import_type"
              :options="[
                { label: 'kbart 2', value: 'kbart' },
                { label: 'custom format', value: 'custom' }
              ]"
              color="tertiary"
              type="radio"
            />
          </div>
          <div v-if="formData.import_type === 'custom'" class="col-md-12">
            <h6 class="m-t-3 m-b-10">Custom format</h6>
            <div class="row q-col-gutter-md q-mb-md">
              <div v-for="(value, key) in formData.custom_columns" :key="key" class="col-xs-3">
                <q-select
                  :id="key"
                  v-model="formData.custom_columns[key]"
                  :name="key"
                  :label="fieldLabel(key)"
                  :options="letters"
                  emit-value
                  color="tertiary"
                  filter
                  autofocus-filter
                  outlined
                />
              </div>
            </div>
          </div>
          <div class="col col-xs-12 m-t-6">
            <q-btn color="tertiary" type="submit">Import title list</q-btn>
          </div>
        </form>
      </q-card-section>
    </q-card>
  </BgImageLayout>
</template>

<script>
import validations from './title-lists-import-validations'
// Libraries
import { cloneDeep } from 'lodash'
import moment from 'moment'
import { mapState } from 'vuex'
// Components
import LmDatePicker from '@/reusable/LmDatePicker/LmDatePicker'

export default {
  name: 'LmPageTitleListsImport',
  components: {
    LmDatePicker
  },

  mixins: [validations],

  data: function() {
    return {
      formData: {
        import_type: 'kbart',
        custom_columns: {},
        valid_from: null,
        valid_to: null,
        file: null,
        name: null
      }
    }
  },

  computed: {
    ...mapState({
      productTitleListData: state => state.titleLists.productTitleListImportData
    }),
    columnsMapping() {
      return this.productTitleListData.columnsMapping
    },
    kbartTemplateUrl() {
      return this.productTitleListData.download
    },
    product() {
      return this.productTitleListData.product
    },
    validationRules() {
      return this.productTitleListData.validationRules
    },
    letters() {
      let options = []
      this.productTitleListData.sheetLetters.map(letter => {
        options.push({
          value: letter,
          label: letter
        })
      })
      return options
    }
  },
  watch: {
    'formData.valid_from'(val) {
      this.formData.valid_to = moment(val, this.settings.date_format)
        .add(1, 'year')
        .subtract(1, 'day')
        .format(this.settings.date_format)
    }
  },
  created() {
    this.formData.name = this.productTitleListData.product.name + ' title list'
  },
  beforeMount() {
    this.formData.custom_columns = cloneDeep(this.columnsMapping)
  },
  methods: {
    process() {
      this.$v.$touch()
      if (this.$v.$invalid) return

      this.sendData(this.product)
    },
    fieldLabel(field) {
      return field.replace(/_/g, ' ')
    },
    async sendData(product) {
      let formData = new FormData()
      formData.append('name', this.formData.name || '')
      formData.append('valid_from', this.formatDateForPost(this.formData.valid_from, this.settings.date_format))
      if (this.formData.valid_to) {
        formData.append('valid_to', this.formatDateForPost(this.formData.valid_to, this.settings.date_format))
      }
      formData.append('import_type', this.formData.import_type)
      formData.append('custom_columns', JSON.stringify(this.formData.custom_columns))
      formData.append('file', this.formData.file)
      this.$store.dispatch('handleGlobalLoader', true, { root: true })
      await this.$store.dispatch('titleLists/processProductTitleList', {
        id: product.id,
        data: formData
      })
      this.$store.dispatch('handleGlobalLoader', false, { root: true })
    },
    download(fileUrl) {
      this.$store.dispatch('downloadFile', fileUrl, { root: true })
    }
  }
}
</script>
